import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { useGetFeatureGateSuggestionsQuery } from '@frameio/graphql-client/generated';
import { useAccountPermissions } from '@frameio/permissions';
import { useActiveAccountId } from '../../contexts';
import { UPLOAD_LINKS_PLAN_FEATURE } from '../constants';

/**
 * Single gate for authenticated Upload Links UI: the rollout flag AND the
 * enterprise plan entitlement must both be satisfied, AND the account must not
 * have the feature explicitly disabled. Returns false while the plan suggestion
 * is still loading, so callers that must distinguish loading (e.g. a route
 * `notFound()` guard) should also check `useFeatureGatesLoading`.
 *
 * The entitlement is read from `GetFeatureGateSuggestions` directly rather than
 * through `useFeatureGatePlanSuggestion`, because `@frameio/plan-management`
 * depends on this package. Under `FeatureGatesContextProvider` in `app` mode
 * this is a cache hit on the provider's query; elsewhere (share review, apps
 * without the provider) it fetches the viewer's active account's suggestions.
 */
export const useIsUploadLinksEnabled = () => {
  const accountId = useActiveAccountId();
  const isFlagEnabled = useFeatureFlagEnabled('v4.upload_links');

  const { data } = useGetFeatureGateSuggestionsQuery({
    variables: { accountId: accountId ?? '' },
    skip: !accountId || !isFlagEnabled,
  });

  const isEntitled =
    data?.account?.subscription?.suggestedPlan?.some(
      (suggestion) =>
        suggestion?.feature?.toLowerCase() ===
          UPLOAD_LINKS_PLAN_FEATURE.toLowerCase() &&
        suggestion.includedInCurrentPlan
    ) ?? false;

  const isNotDisabledForAccount = useAccountPermissions({
    accountId: accountId ?? '',
    check: 'canAccessUploadLinksFeature',
    isInitiallyPermitted: true,
  });

  return isFlagEnabled && isEntitled && isNotDisabledForAccount;
};
